// Iteration 119 -- Minimal player home page.
// Read-only landing for the `player` role. Shows the player's name +
// school, their teams, and upcoming events in the next 14 days.
// 143 -- POPIA: a minor player is blocked (403 guardian_consent_required)
// until a linked guardian records consent; we render a waiting state.

import { useEffect, useState } from "react";
import type { MePlayerResponse } from "@dugads/shared";
import { ShieldAlert } from "lucide-react";
import * as api from "@/lib/api";
import { AvailabilityRequestsCard } from "@/components/availability-requests-card";

const formatEventDateTime = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function PlayerHomePage() {
  const [data, setData] = useState<MePlayerResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .getMePlayer()
      .then((res) => {
        setData(res);
        setError(null);
        setBlocked(false);
      })
      .catch((e) => {
        // 143 -- POPIA guardian-consent gate: surface a waiting state, not an error.
        if (e instanceof api.ApiError && e.status === 403 && e.body?.error === "guardian_consent_required") {
          setBlocked(true);
          setError(null);
          return;
        }
        setError(e instanceof api.ApiError ? e.message : "Failed to load");
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <p className="text-muted-foreground" data-page="player-home" data-state="loading">
        Loading...
      </p>
    );
  }
  if (blocked) {
    return (
      <div
        className="mx-auto flex max-w-md flex-col items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 p-6 text-center"
        data-page="player-home"
        data-state="blocked"
      >
        <ShieldAlert className="h-8 w-8 text-amber-600" />
        <h1 className="text-lg font-semibold text-amber-900">Waiting for parent/guardian approval</h1>
        <p className="text-sm text-amber-800">
          Before you can see your teams and fixtures, a parent or guardian needs to approve your
          account. Ask them to sign in and approve you from their "My Children" page. If they
          haven't linked to you yet, share your family code with them.
        </p>
      </div>
    );
  }
  if (error) {
    return (
      <p className="text-sm text-destructive" data-page="player-home" data-state="error" data-error>
        {error}
      </p>
    );
  }
  if (!data) {
    return (
      <p className="text-muted-foreground" data-page="player-home" data-state="empty">
        No player record linked to your account.
      </p>
    );
  }

  const fullName = [data.player.first_name, data.player.surname].filter(Boolean).join(" ");

  return (
    <div className="flex flex-col gap-6" data-page="player-home" data-state="ready">
      <div data-region="header">
        <h1 className="text-2xl font-semibold">{fullName || "My Home"}</h1>
        {data.player.school && (
          <p className="text-muted-foreground">{data.player.school.name}</p>
        )}
      </div>

      {/* 138 -- pending availability requests the player can answer for themselves */}
      <AvailabilityRequestsCard />

      <section className="flex flex-col gap-3 rounded-lg border border-border p-4" data-region="teams">
        <h2 className="text-lg font-medium">My Teams ({data.teams.length})</h2>
        {data.teams.length === 0 ? (
          <p className="text-sm text-muted-foreground">You haven't been added to any teams yet.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {data.teams.map((t) => (
              <li
                key={t.id}
                className="flex flex-col gap-0.5 rounded-md border border-border bg-card px-3 py-2"
                data-team-id={t.id}
              >
                <span className="font-medium">{t.name}</span>
                <span className="text-xs text-muted-foreground">
                  {t.age_group}
                  {t.sport ? ` · ${t.sport}` : ""}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section
        className="flex flex-col gap-3 rounded-lg border border-border p-4"
        data-region="upcoming-events"
      >
        <h2 className="text-lg font-medium">
          Upcoming Events ({data.upcoming_events.length})
        </h2>
        {data.upcoming_events.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No upcoming events in the next 14 days for your teams.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {data.upcoming_events.map((e) => (
              <li
                key={e.id}
                className="flex flex-col gap-0.5 rounded-md border border-border bg-card px-3 py-2"
                data-event-id={e.id}
              >
                <span className="font-medium">{e.name}</span>
                <span className="text-xs text-muted-foreground">
                  {formatEventDateTime(e.starts_at)}
                  {e.venue ? ` · ${e.venue}` : ""}
                  {e.school?.name ? ` · ${e.school.name}` : ""}
                </span>
                {e.my_teams.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    Playing for: {e.my_teams.join(", ")}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
